/**
 * Proceduralne tekstury placu zabaw (Toca Boca) — gdy brak PNG obj_park_*.
 * Zero napisów / emoji tekstowych; same kształty i kolory.
 */
import Phaser from 'phaser';
import {
  PARK_OBJECT_DEFS,
  PARK_PLACEMENTS,
  PARK_SANDBOX_DEF_ID,
  PARK_SLIDE_DEF_ID,
  PARK_SWING_DEF_ID,
  PARK_SANDCAKE_DEF_ID,
  PARK_BENCH_DEF_ID,
} from './objects.js';

const TEX_SIZE = 160;

export type PlaygroundDecorKind =
  | 'slide'
  | 'swing'
  | 'sandbox'
  | 'sandcake'
  | 'bench';

/** Mapa texture key → rodzaj rysunku. */
export const PLAYGROUND_TEXTURE_SHAPES: Readonly<
  Record<string, PlaygroundDecorKind>
> = {
  obj_park_slide: 'slide',
  obj_park_swing: 'swing',
  obj_park_sandbox: 'sandbox',
  obj_park_sandcake: 'sandcake',
  obj_park_bench: 'bench',
};

/** Pozycje soft-padów pod sprzętem (dekor, bez tekstu). */
export function playgroundGroundPads(): readonly {
  defId: string;
  x: number;
  y: number;
  color: number;
  w: number;
  h: number;
}[] {
  return PARK_PLACEMENTS.map((p) => {
    if (p.defId === PARK_SLIDE_DEF_ID) {
      return { defId: p.defId, x: p.x, y: p.y + 36, color: 0x90caf9, w: 110, h: 36 };
    }
    if (p.defId === PARK_SWING_DEF_ID) {
      return { defId: p.defId, x: p.x, y: p.y + 40, color: 0xffcc80, w: 100, h: 32 };
    }
    if (p.defId === PARK_SANDBOX_DEF_ID) {
      return { defId: p.defId, x: p.x, y: p.y + 28, color: 0xffe082, w: 140, h: 48 };
    }
    if (p.defId === PARK_BENCH_DEF_ID) {
      return { defId: p.defId, x: p.x, y: p.y + 24, color: 0xd7ccc8, w: 130, h: 30 };
    }
    return { defId: p.defId, x: p.x, y: p.y + 20, color: 0xffe0b2, w: 80, h: 28 };
  });
}

/**
 * Upewnij się, że wszystkie tekstury placu zabaw istnieją.
 * PNG z Preload mają pierwszeństwo; brak → narysuj proceduralnie.
 */
export function ensurePlaygroundTextures(scene: Phaser.Scene): void {
  for (const def of PARK_OBJECT_DEFS) {
    ensurePlaygroundTexture(scene, def.texture);
  }
}

export function ensurePlaygroundTexture(
  scene: Phaser.Scene,
  textureKey: string,
): string {
  if (scene.textures.exists(textureKey)) return textureKey;
  const kind = PLAYGROUND_TEXTURE_SHAPES[textureKey];
  if (!kind) return textureKey;

  const g = scene.make.graphics({ x: 0, y: 0 });
  drawPlaygroundShape(g, kind, TEX_SIZE, TEX_SIZE);
  g.generateTexture(textureKey, TEX_SIZE, TEX_SIZE);
  g.destroy();
  return textureKey;
}

/** Czysty opis kształtu (testowalny). */
export function playgroundShapeForTexture(
  textureKey: string,
): PlaygroundDecorKind | null {
  return PLAYGROUND_TEXTURE_SHAPES[textureKey] ?? null;
}

export function drawPlaygroundShape(
  g: Phaser.GameObjects.Graphics,
  kind: PlaygroundDecorKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;

  switch (kind) {
    case 'slide':
      drawSlide(g, cx, cy);
      break;
    case 'swing':
      drawSwing(g, cx, cy);
      break;
    case 'sandbox':
      drawSandbox(g, cx, cy);
      break;
    case 'sandcake':
      drawSandcake(g, cx, cy);
      break;
    case 'bench':
      drawBench(g, cx, cy);
      break;
  }
}

function drawSlide(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // platforma + drabinka + zjeżdżnia
  g.fillStyle(0x66bb6a, 1);
  g.fillRoundedRect(cx - 48, cy - 50, 40, 70, 8);
  // szczeble
  g.fillStyle(0xffffff, 0.85);
  for (let i = 0; i < 4; i++) {
    g.fillRect(cx - 42, cy - 40 + i * 14, 28, 5);
  }
  // rura zjeżdżalni (skośna)
  g.fillStyle(0x42a5f5, 1);
  g.fillTriangle(cx - 12, cy - 48, cx + 52, cy + 40, cx + 20, cy + 48);
  g.fillTriangle(cx - 12, cy - 48, cx + 20, cy + 48, cx - 4, cy - 20);
  // miękkie zakończenie toru + jasny ślizg
  g.fillRoundedRect(cx + 16, cy + 34, 46, 16, 8);
  g.lineStyle(5, 0x90caf9, 0.95);
  g.lineBetween(cx - 6, cy - 42, cx + 44, cy + 36);
  // poręcz
  g.lineStyle(4, 0xffffff, 0.95);
  g.lineBetween(cx - 12, cy - 48, cx + 43, cy + 38);
  g.fillStyle(0xffd166, 1);
  g.fillCircle(cx - 12, cy - 48, 6);
  g.fillCircle(cx + 48, cy + 42, 5);
  // cień podstawy
  g.fillStyle(0x81c784, 0.6);
  g.fillEllipse(cx + 10, cy + 48, 70, 16);
}

function drawSwing(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // rama A
  g.lineStyle(8, 0xa1887f, 1);
  g.lineBetween(cx - 50, cy + 48, cx, cy - 52);
  g.lineBetween(cx + 50, cy + 48, cx, cy - 52);
  g.lineStyle(7, 0x8d6e63, 1);
  g.lineBetween(cx - 36, cy - 40, cx + 36, cy - 40);
  // łańcuszki
  g.lineStyle(3, 0xb0bec5, 1);
  g.lineBetween(cx - 18, cy - 40, cx - 18, cy + 18);
  g.lineBetween(cx + 18, cy - 40, cx + 18, cy + 18);
  // siedzisko
  g.fillStyle(0xff8a65, 1);
  g.fillRoundedRect(cx - 30, cy + 10, 60, 22, 8);
  g.lineStyle(3, 0xffffff, 0.9);
  g.strokeRoundedRect(cx - 30, cy + 10, 60, 22, 8);
  // boczne uchwyty jak bezpieczny fotelik
  g.lineStyle(4, 0xffd166, 1);
  g.lineBetween(cx - 28, cy + 12, cx - 34, cy - 4);
  g.lineBetween(cx + 28, cy + 12, cx + 34, cy - 4);
  g.fillStyle(0xffffff, 0.55);
  g.fillRoundedRect(cx - 20, cy + 14, 40, 5, 2);
  // trawa pod
  g.fillStyle(0xa5d6a7, 0.7);
  g.fillEllipse(cx, cy + 52, 90, 18);
}

function drawSandbox(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // skrzynia
  g.fillStyle(0xd7a86e, 1);
  g.fillRoundedRect(cx - 58, cy - 20, 116, 56, 10);
  g.lineStyle(4, 0xffffff, 0.9);
  g.strokeRoundedRect(cx - 58, cy - 20, 116, 56, 10);
  // piasek
  g.fillStyle(0xffe082, 1);
  g.fillEllipse(cx, cy + 4, 100, 40);
  // foremki (bez tekstu)
  g.fillStyle(0xef9a9a, 1);
  g.fillCircle(cx - 24, cy, 10);
  g.fillStyle(0x80cbc4, 1);
  g.fillTriangle(cx + 10, cy - 10, cx + 28, cy + 10, cx - 4, cy + 10);
  g.fillStyle(0x90caf9, 1);
  g.fillRoundedRect(cx + 18, cy - 6, 18, 16, 4);
}

function drawSandcake(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // babka z piasku (warstwy)
  g.fillStyle(0xffe082, 1);
  g.fillEllipse(cx, cy + 22, 56, 20);
  g.fillEllipse(cx, cy + 6, 42, 18);
  g.fillEllipse(cx, cy - 8, 28, 16);
  g.fillStyle(0xffcc80, 1);
  g.fillEllipse(cx, cy - 18, 18, 12);
  // flaga-patyczek (bez napisu)
  g.lineStyle(3, 0xa1887f, 1);
  g.lineBetween(cx, cy - 18, cx, cy - 40);
  g.fillStyle(0xff8a65, 1);
  g.fillTriangle(cx, cy - 40, cx + 16, cy - 34, cx, cy - 28);
}

function drawBench(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // deska siedziska
  g.fillStyle(0xc4a484, 1);
  g.fillRoundedRect(cx - 56, cy - 4, 112, 22, 6);
  g.lineStyle(3, 0xffffff, 0.85);
  g.strokeRoundedRect(cx - 56, cy - 4, 112, 22, 6);
  // oparcie
  g.fillStyle(0xb08968, 1);
  g.fillRoundedRect(cx - 54, cy - 36, 108, 18, 5);
  // nogi
  g.fillStyle(0x8d6e63, 1);
  g.fillRect(cx - 48, cy + 16, 10, 28);
  g.fillRect(cx + 38, cy + 16, 10, 28);
  g.fillRect(cx - 48, cy - 34, 8, 30);
  g.fillRect(cx + 40, cy - 34, 8, 30);
  // cień
  g.fillStyle(0xa5d6a7, 0.55);
  g.fillEllipse(cx, cy + 48, 100, 16);
}

/** Klucze tekstur sprzętu (do testów / preload). */
export function playgroundTextureKeys(): string[] {
  return Object.keys(PLAYGROUND_TEXTURE_SHAPES);
}

void PARK_SANDCAKE_DEF_ID;
void PARK_BENCH_DEF_ID;
